import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VerificationResult = ({ result, onClose, onSaveReport }) => {
  if (!result) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return { name: 'CheckCircle', color: 'text-success' };
      case 'invalid':
        return { name: 'XCircle', color: 'text-error' };
      case 'expired':
        return { name: 'Clock', color: 'text-warning' };
      case 'revoked':
        return { name: 'Ban', color: 'text-error' };
      default:
        return { name: 'AlertCircle', color: 'text-muted-foreground' };
    }
  };

  const getCredentialTypeIcon = (type) => {
    switch (type) {
      case 'vaccination_proof':
        return 'Shield';
      case 'insurance_coverage':
        return 'Heart';
      case 'training_certificate':
        return 'GraduationCap';
      case 'prescription_auth':
        return 'Pill';
      default:
        return 'FileText';
    }
  };

  const statusIcon = getStatusIcon(result?.status);
  const typeIcon = getCredentialTypeIcon(result?.credentialType);

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            result?.status === 'valid' ? 'bg-success/10' : 
            result?.status === 'invalid'|| result?.status === 'revoked' ? 'bg-error/10' : 'bg-warning/10'
          }`}>
            <Icon name={statusIcon?.name} size={24} className={statusIcon?.color} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Verification Result</h2>
            <p className={`text-sm font-medium ${statusIcon?.color}`}>
              {result?.status?.charAt(0)?.toUpperCase() + result?.status?.slice(1)}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <Icon name="X" size={16} />
        </Button>
      </div>
      {/* Credential Information */}
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Credential Type</label>
              <div className="flex items-center space-x-2 mt-1">
                <Icon name={typeIcon} size={16} className="text-primary" />
                <span className="text-foreground">{result?.credentialName}</span>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground">Credential ID</label>
              <p className="text-foreground font-mono text-sm mt-1">{result?.credentialId}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Subject</label>
              <p className="text-foreground mt-1">{result?.subjectName}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Issuer</label>
              <div className="mt-1">
                <p className="text-foreground font-medium">{result?.issuerName}</p>
                <p className="text-sm text-muted-foreground">{result?.issuerDID}</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Issue Date</label>
              <p className="text-foreground mt-1">{formatDate(result?.issuanceDate)}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Expiration</label>
              <p className="text-foreground mt-1">
                {result?.expirationDate ? formatDate(result?.expirationDate) : 'No expiration'}
              </p>
            </div>
          </div>
        </div>

        {/* Validation Details */}
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Validation Details</h3>
          <div className="space-y-3">
            {result?.validationChecks?.map((check, index) => (
              <div key={index} className="flex items-center space-x-3">
                <Icon 
                  name={check?.passed ? 'Check' : 'X'} 
                  size={16} 
                  className={check?.passed ? 'text-success' : 'text-error'} 
                />
                <span className="text-foreground">{check?.description}</span>
                {check?.details && (
                  <span className="text-sm text-muted-foreground">({check?.details})</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Blockchain Verification */}
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Blockchain Verification</h3>
          <div className="bg-muted rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <label className="font-medium text-muted-foreground">Transaction Hash</label>
                <p className="text-foreground font-mono mt-1">{result?.blockchainProof?.txHash}</p>
              </div>
              <div>
                <label className="font-medium text-muted-foreground">Block Number</label>
                <p className="text-foreground mt-1">{result?.blockchainProof?.blockNumber}</p>
              </div>
              <div>
                <label className="font-medium text-muted-foreground">Network</label>
                <p className="text-foreground mt-1">{result?.blockchainProof?.network}</p>
              </div>
              <div>
                <label className="font-medium text-muted-foreground">Confirmation Time</label>
                <p className="text-foreground mt-1">{formatDate(result?.blockchainProof?.timestamp)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Zero-Knowledge Proofs (if applicable) */}
        {result?.zkProofs && result?.zkProofs?.length > 0 && (
          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-medium text-foreground mb-4">Privacy-Preserving Proofs</h3>
            <div className="space-y-3">
              {result?.zkProofs?.map((proof, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="Shield" size={16} className="text-primary" />
                    <div>
                      <p className="text-foreground font-medium">{proof?.type}</p>
                      <p className="text-sm text-muted-foreground">{proof?.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm text-success">Verified</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="border-t border-border pt-6">
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            <Button variant="default" onClick={onSaveReport}>
              <Icon name="Download" size={16} className="mr-2" />
              Download Report
            </Button>
            <Button variant="outline">
              <Icon name="Share" size={16} className="mr-2" />
              Share Result
            </Button>
            <Button variant="outline">
              <Icon name="Copy" size={16} className="mr-2" />
              Copy Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationResult;